import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';

import Table from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';

import { Button, Checkbox, Typography } from '@mui/material';
import {
  CheckCircle,
  KeyboardDoubleArrowRightOutlined,
} from '@mui/icons-material';
import ButtonGroup from 'components/ButtonGroup';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { TransactionOperation } from 'further-types/lp/transaction';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createLpPeriodEndReviewDetailRoute,
  LP_PERIOD_END_REVIEW,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import ProcessingPage from 'components/new/ProcessingPage';
import Chip from 'components/Chip';

enum ApprovePrepaymentsTab {
  ResolvedPayments = 'resolvedPayments',
  ResolvedDeferredIncome = 'resolvedDeferredIncome',
}

const tabsData = {
  [ApprovePrepaymentsTab.ResolvedPayments]: { title: 'Resolved payments' },
  [ApprovePrepaymentsTab.ResolvedDeferredIncome]: {
    title: 'Resolved deferred income',
  },
};

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

const pageId = 'approve-prepayments';

const ApprovePrepayments: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const periodId = searchParams.get('periodId') ?? undefined;
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const history = useHistory();
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [activeTab, setActiveTab] = useState<ApprovePrepaymentsTab>(
    ApprovePrepaymentsTab.ResolvedPayments,
  );
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);
  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'ledgerDate',
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const handleMutation = useHandleTrpcMutation<string>();
  const updateChecklistItem =
    api.accountingPeriod.updateChecklistItem.useMutation(
      handleMutation({ refetchType: 'active' }),
    );

  const handleExit = () => {
    history.push(
      periodId
        ? createLpPeriodEndReviewDetailRoute(periodId)
        : LP_PERIOD_END_REVIEW,
    );
  };

  const handleApprove = () => {
    if (!selectedVehicleId || !periodId) return;
    setIsProcessingApproval(true);
    updateChecklistItem.mutate(
      {
        vehicleId: selectedVehicleId,
        id: periodId,
        item: 'prepaymentResolutions',
        confirmed: true,
      },
      {
        onSuccess: () =>
          history.push(createLpPeriodEndReviewDetailRoute(periodId)),
        onError: () => setIsProcessingApproval(false),
      },
    );
  };

  const queryEnabled = !!periodStart && !!periodEnd;
  const periodParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? undefined,
    periodEnd: periodEnd ?? undefined,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  };

  const { data: resolvedPaymentsData } =
    api.prepayments.listPrepayments.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Spent },
      { enabled: queryEnabled },
    );

  const { data: resolvedDeferredIncomeData } =
    api.prepayments.listPrepayments.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Received },
      { enabled: queryEnabled },
    );

  const countParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? undefined,
    periodEnd: periodEnd ?? undefined,
    showUnapprovedOnly: true,
    page: 1,
    rowsPerPage: 1,
    order: 'desc' as const,
    orderBy: 'ledgerDate' as const,
  };

  const { data: resolvedPaymentsUnapproved } =
    api.prepayments.listPrepayments.useQuery(
      { ...countParams, actionOperationType: ActionOperationType.Spent },
      { enabled: queryEnabled },
    );

  const { data: resolvedDeferredIncomeUnapproved } =
    api.prepayments.listPrepayments.useQuery(
      { ...countParams, actionOperationType: ActionOperationType.Received },
      { enabled: queryEnabled },
    );

  const tabData = {
    [ApprovePrepaymentsTab.ResolvedPayments]: resolvedPaymentsData,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]: resolvedDeferredIncomeData,
  };

  const unapprovedCounts = {
    [ApprovePrepaymentsTab.ResolvedPayments]:
      resolvedPaymentsUnapproved?.totalCount,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]:
      resolvedDeferredIncomeUnapproved?.totalCount,
  };

  const data = tabData[activeTab];

  const cleanupState = () => {
    setSelectedRows([]);
    setTransactionChanges(new Map());
    setExpandedRowId(null);
  };

  const handleTabChange = (tab: ApprovePrepaymentsTab) => {
    cleanupState();
    setActiveTab(tab);
    pagination.handleChangeSort('ledgerDate', 'desc');
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });
  };

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: approvePrepayments, isPending } =
    api.prepayments.approvePrepayments.useMutation(
      mutationOpts({
        successMessage: 'Prepayments approved successfully',
        onSuccess: () => cleanupState(),
      }),
    );

  const approveTransactions = () => {
    const approvePrepaymentsBody = selectedRows.map((rowId) => {
      const transactions = transactionChanges.get(rowId);
      return {
        actionId: rowId,
        transactions: transactions ?? [],
      };
    });
    approvePrepayments({
      vehicleId: selectedVehicleId ?? '',
      prepayments: approvePrepaymentsBody,
    });
  };

  const rowsForTable = (data?.results ?? []).map((row) => ({
    ...row,
    id: row.id ?? '',
  }));

  const columns = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'amountTcy',
      label: 'Amount (TCY)',
      render: (row) => numberToCurrencyString(row.amountTcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) => numberToCurrencyString(row.amountLcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
    {
      key: 'isAccountScheduleAuthorised',
      label: 'Approved',
      render: (row) => {
        if (row.isAccountScheduleAuthorised) {
          return <Checkbox checked disabled />;
        }
        const rowId = row.id!;
        return (
          <Checkbox
            checked={selectedRows.includes(rowId)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRows((prev) => [...prev, rowId]);
              } else {
                setSelectedRows((prev) => prev.filter((id) => id !== rowId));
              }
            }}
          />
        );
      },
      disableSorting: true,
    },
  ];

  return (
    <Page
      heading="Automated Transactions: Resolve prepayments"
      headingIcon={KeyboardDoubleArrowRightOutlined}
      spacing="lg"
      withHeadingDivider
      actionBar={
        <ButtonGroup align="stretch">
          <Button variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {periodId && (
            <Button variant="contained" onClick={handleApprove}>
              Approve and mark as reviewed
            </Button>
          )}
        </ButtonGroup>
      }
    >
      <PageContent withSpacing>
        {isProcessingApproval ? (
          <PageSection withSpacing>
            <ProcessingPage
              title="Approving transactions"
              subtitle="This will only take a moment."
            />
          </PageSection>
        ) : (
          <PageSection withSpacing>
            <Typography variant="h5">Resolve prepayments</Typography>
            <Typography variant="body2">
              Review and approve prepayment resolutions from{' '}
              {periodStart?.toLabel()} – {periodEnd?.toLabel()}.
            </Typography>
            <TabsPanel
              tabs={Object.entries(tabsData).map(([tab, { title }]) => ({
                key: tab as ApprovePrepaymentsTab,
                label: (
                  <TabLabelWrapper>
                    {title}
                    {unapprovedCounts[tab as ApprovePrepaymentsTab] !==
                      undefined &&
                      (unapprovedCounts[tab as ApprovePrepaymentsTab] === 0 ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Chip variant="tabIndicator" small>
                          {unapprovedCounts[tab as ApprovePrepaymentsTab]}
                        </Chip>
                      ))}
                  </TabLabelWrapper>
                ),
                content: (
                  <>
                    <Table
                      columns={columns}
                      rows={rowsForTable}
                      pagination={{
                        page: pagination.page,
                        rowsPerPage: pagination.rowsPerPage,
                        order: pagination.order,
                        orderBy: pagination.orderBy,
                        handleRequestSort: pagination.handleRequestSort,
                        handleChangePage: pagination.handleChangePage,
                        handleChangeRowsPerPage:
                          pagination.handleChangeRowsPerPage,
                      }}
                      rowsTotalCount={data?.totalCount ?? 0}
                      isExpandableRow={() => true}
                      isExpandedRow={(row) => expandedRowId === row.id}
                      onRowExpand={(row) =>
                        setExpandedRowId((prev) =>
                          prev === row.id ? null : row.id,
                        )
                      }
                      getExpandedContent={(row) => (
                        <ExpandedRowContainer>
                          {!transactionTypesLoading && vehicle && (
                            <TransactionAllocator
                              showCurrencyRate
                              transactionTypeOptions={transactionTypeOptions}
                              transactions={row.transactions ?? []}
                              readOnly={row.isAccountScheduleAuthorised}
                              onChange={(transactions) => {
                                handleTransactionChange(
                                  row.id ?? '',
                                  transactions,
                                );
                              }}
                              transactionCurrency={row.currencyCode}
                              vehicle={vehicle}
                              fxRate={null}
                              darkBackground
                            />
                          )}
                        </ExpandedRowContainer>
                      )}
                    />
                    {selectedRows.length > 0 && (
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={approveTransactions}
                          disabled={isPending}
                        >
                          Approve {selectedRows.length} automated{' '}
                          {pluralize('transaction', selectedRows.length)}
                        </Button>
                      </ButtonGroup>
                    )}
                  </>
                ),
              }))}
              onTabChange={handleTabChange}
              defaultTab={ApprovePrepaymentsTab.ResolvedPayments}
            />
          </PageSection>
        )}
      </PageContent>
    </Page>
  );
};

export default ApprovePrepayments;
